import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { StaticPageLayout } from '../layouts/static-page-layout';
import { AceRenderer, InlineAceRenderer } from '@/components/ui/ace-renderer';
import { analyzeContent } from '@/lib/content-processor';
import { BookOpen, Calculator, Database, Beaker, TestTube, Zap } from 'lucide-react';

// Real mathematical content samples
const realMathContent = {
  introduction: "In this chapter, you have studied the following points:\n1). If a ray stands on a line, then the sum of the two adjacent angles so formed is $180\\degree$ and vice versa. This property is called as the Linear pair axiom.\n\n2). If two lines intersect each other, then the vertically opposite angles are equal.\n\n3). If a transversal intersects two parallel lines, then\n(i) each pair of corresponding angles is equal,\n(ii) each pair of alternate interior angles is equal,\n(iii) each pair of interior angles on the same side of the transversal is supplementary.\n\n4). If a transversal intersects two lines such that, either\n(i) any one pair of corresponding angles is equal, or\n(ii) any one pair of alternate interior angles is equal, or\n(iii) any one pair of interior angles on the same side of the transversal is supplementary, then the lines are parallel.\n\n5). Lines which are parallel to a given line are parallel to each other.\n\n6). The sum of the three angles of a triangle is $180\\degree$.\n\n7). If a side of a triangle is produced, the exterior angle so formed is equal to the sum of the two interior opposite angles.",

  quotes: "## Quote Environments Test\n\n### Standard LaTeX Quote\n\\begin{quote}\nThe principal use of the Analytic Art is to bring Mathematical Problems to Equations and to exhibit those Equations in the most simple terms that can be.\n\\end{quote}\n—Edmund Halley\n\n### Quote with Math\n\\begin{quote}\nIn mathematics, the derivative of $f(x) = x^2$ is $f'(x) = 2x$. This fundamental concept allows us to understand rates of change.\n\\end{quote}\n\n### Multiple Quotes\n\\begin{quote}\nFirst quote with important mathematical insight.\n\\end{quote}\n\n\\begin{quote}\nSecond quote demonstrating proper LaTeX quote environment handling.\n\\end{quote}",

  trigonometry: "## Trigonometric Identities\n\nIn a right triangle ABC with angle A:\n\n### Basic Ratios\n- $\\sin A = \\frac{\\text{opposite}}{\\text{hypotenuse}}$\n- $\\cos A = \\frac{\\text{adjacent}}{\\text{hypotenuse}}$\n- $\\tan A = \\frac{\\text{opposite}}{\\text{adjacent}}$\n\n### Complementary Angles\nFor complementary angles:\n$$\\begin{align}\n\\sin(90° - A) &= \\cos A \\\\\n\\cos(90° - A) &= \\sin A \\\\\n\\tan(90° - A) &= \\cot A\n\\end{align}$$",

  calculus: "## Calculus Examples\n\n### Derivatives\nThe derivative of $f(x) = x^n$ is:\n$$f'(x) = nx^{n-1}$$\n\n### Integration\nThe fundamental theorem of calculus:\n$$\\int_a^b f'(x) dx = f(b) - f(a)$$\n\n### Example\nFind $\\int_0^1 x^2 dx$:\n$$\\int_0^1 x^2 dx = \\left[\\frac{x^3}{3}\\right]_0^1 = \\frac{1}{3} - 0 = \\frac{1}{3}$$",

  geometry: "## Geometric Theorems\n\n### Pythagorean Theorem\nIn a right triangle with sides $a$, $b$, and hypotenuse $c$:\n$$a^2 + b^2 = c^2$$\n\n### Circle Area and Circumference\n- **Area**: $A = \\pi r^2$\n- **Circumference**: $C = 2\\pi r$\n\n### Volume of a Sphere\n$$V = \\frac{4}{3}\\pi r^3$$",

  algebra: "## Algebraic Expressions\n\n### Quadratic Formula\nFor equation $ax^2 + bx + c = 0$ where $a \\neq 0$:\n$$x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}$$\n\n### Logarithmic Properties\n- $\\log_a(xy) = \\log_a x + \\log_a y$\n- $\\log_a\\left(\\frac{x}{y}\\right) = \\log_a x - \\log_a y$\n- $\\log_a(x^n) = n\\log_a x$",

  probability: "## Probability and Statistics\n\n### Basic Probability\nFor event A: $P(A) = \\frac{\\text{favorable outcomes}}{\\text{total outcomes}}$\n\n### Normal Distribution\nProbability density function:\n$$f(x) = \\frac{1}{\\sigma\\sqrt{2\\pi}}e^{-\\frac{(x-\\mu)^2}{2\\sigma^2}}$$\n\n### Standard Deviation\n$$\\sigma = \\sqrt{\\frac{\\sum(x_i - \\bar{x})^2}{n}}$$"
};

const LatexDemoPage: React.FC = () => {
  const [selectedContent, setSelectedContent] = useState<keyof typeof realMathContent>('introduction');
  const [customContent, setCustomContent] = useState('### Custom Mathematical Content\n\nEnter your content with **markdown** and $\\LaTeX$ expressions...\n\n#### Example Expressions:\n- Inline: The area of a circle is $A = \\pi r^2$\n- Block equation:\n\n$$\\int_{-\\infty}^{\\infty} e^{-x^2} dx = \\sqrt{\\pi}$$\n\n#### Fractions and Complex Expressions:\n$$\\frac{d}{dx}\\left(\\frac{\\sin x}{\\cos x}\\right) = \\sec^2 x$$');
  const [showDebug, setShowDebug] = useState(false);
  const [enableMath, setEnableMath] = useState(true);
  const [livePreview, setLivePreview] = useState(true);

  const currentContent = realMathContent[selectedContent];
  const analysis = analyzeContent(currentContent);
  const customAnalysis = analyzeContent(customContent);

  return (
    <StaticPageLayout>
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-4">
            LaTeX Renderer Demo
          </h1>
          <p className="text-xl text-muted-foreground mb-6">
            Test and validate mathematical expressions with AceRenderer - the intelligent LaTeX processor
          </p>
          
          <div className="flex justify-center gap-4 flex-wrap">
            <Badge variant="secondary" className="flex items-center gap-1">
              <Calculator className="w-3 h-3" />
              LaTeX Math
            </Badge>
            <Badge variant="secondary" className="flex items-center gap-1">
              <BookOpen className="w-3 h-3" />
              Educational Content
            </Badge>
            <Badge variant="secondary" className="flex items-center gap-1">
              <TestTube className="w-3 h-3" />
              Live Testing
            </Badge>
            <Badge variant="secondary" className="flex items-center gap-1">
              <Zap className="w-3 h-3" />
              Real-time Preview
            </Badge>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Sidebar - Content Selection & Controls */}
          <div className="lg:col-span-3">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Sample Content</CardTitle>
                <CardDescription>
                  Real mathematical content from educational materials
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {Object.entries(realMathContent).map(([key, content]) => {
                  const contentAnalysis = analyzeContent(content);
                  return (
                    <Button
                      key={key}
                      variant={selectedContent === key ? 'default' : 'ghost'}
                      className="w-full justify-start text-left h-auto p-3"
                      onClick={() => setSelectedContent(key as keyof typeof realMathContent)}
                    >
                      <div className="flex flex-col items-start w-full">
                        <div className="font-medium text-sm capitalize">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 flex gap-2">
                          <span>{contentAnalysis.wordCount} words</span>
                          <span>{contentAnalysis.mathExpressions.total} math</span>
                        </div>
                      </div>
                    </Button>
                  );
                })}
              </CardContent>
            </Card>

            {/* Controls */}
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="text-sm">Renderer Controls</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="debug-toggle" className="text-sm">Debug Mode</Label>
                  <Button
                    id="debug-toggle"
                    variant={showDebug ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setShowDebug(!showDebug)}
                  >
                    {showDebug ? 'ON' : 'OFF'}
                  </Button>
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="math-toggle" className="text-sm">LaTeX Rendering</Label>
                  <Button
                    id="math-toggle"
                    variant={enableMath ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setEnableMath(!enableMath)}
                  >
                    {enableMath ? 'ON' : 'OFF'}
                  </Button>
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="live-toggle" className="text-sm">Live Preview</Label>
                  <Button
                    id="live-toggle"
                    variant={livePreview ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setLivePreview(!livePreview)}
                  >
                    {livePreview ? 'ON' : 'OFF'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Content Analysis */}
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="text-sm">Content Analysis</CardTitle>
              </CardHeader>
              <CardContent className="text-xs space-y-2">
                <div className="flex justify-between">
                  <span>Words:</span>
                  <Badge variant="outline">{analysis.wordCount}</Badge>
                </div>
                <div className="flex justify-between">
                  <span>Inline Math:</span>
                  <Badge variant="outline">{analysis.mathExpressions.inline}</Badge>
                </div>
                <div className="flex justify-between">
                  <span>Block Math:</span>
                  <Badge variant="outline">{analysis.mathExpressions.block}</Badge>
                </div>
                <div className="flex justify-between">
                  <span>Lists:</span>
                  <Badge variant="outline">{analysis.hasLists ? 'Yes' : 'No'}</Badge>
                </div>
                <div className="flex justify-between">
                  <span>Markdown:</span>
                  <Badge variant="outline">{analysis.hasMarkdown ? 'Yes' : 'No'}</Badge>
                </div>
                <div className="flex justify-between">
                  <span>Complexity:</span>
                  <Badge variant={analysis.estimatedComplexity > 5 ? 'destructive' : 'default'}>
                    {analysis.estimatedComplexity}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-9">
            {/* Sample Content Renderer */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="capitalize">
                    {selectedContent.replace(/([A-Z])/g, ' $1').trim()} - Live Render
                  </span>
                  <Badge variant="outline" className="flex items-center gap-1">
                    <Beaker className="w-3 h-3" />
                    AceRenderer
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Real mathematical content rendered with intelligent LaTeX processing
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AceRenderer
                  content={currentContent}
                  showDebug={showDebug}
                  enableMath={enableMath}
                  showAnalysis={true}
                  onMathError={(error: Error, expression: string) => {
                    console.error('LaTeX Error:', error, 'Expression:', expression);
                  }}
                />
              </CardContent>
            </Card>

            {/* Interactive Content Editor */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TestTube className="w-5 h-5" />
                  Interactive LaTeX Editor
                </CardTitle>
                <CardDescription>
                  Test your own mathematical expressions and content with live preview
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid lg:grid-cols-2 gap-4">
                  {/* Editor */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="custom-content">Input Editor</Label>
                      <div className="flex gap-2 text-xs">
                        <Badge variant="secondary">{customAnalysis.wordCount} words</Badge>
                        <Badge variant="secondary">{customAnalysis.mathExpressions.total} math</Badge>
                      </div>
                    </div>
                    <Textarea
                      id="custom-content"
                      value={customContent}
                      onChange={(e) => setCustomContent(e.target.value)}
                      rows={12}
                      className="font-mono text-sm"
                      placeholder="Enter content with **markdown** and $\LaTeX$ expressions..."
                    />
                  </div>
                  
                  {/* Live Preview */}
                  <div className="space-y-2">
                    <Label>Live Preview</Label>
                    <div className="border rounded-lg p-4 bg-slate-50 min-h-[300px] overflow-auto">
                      {livePreview ? (
                        <AceRenderer
                          content={customContent}
                          enableMath={enableMath}
                          showDebug={false}
                        />
                      ) : (
                        <div className="text-muted-foreground text-sm">
                          Live preview disabled. Enable it in controls to see real-time rendering.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Test Examples */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Quick Test Examples</CardTitle>
                <CardDescription>
                  Click to load common mathematical expressions into the editor
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {[
                    {
                      name: "Quadratic Formula",
                      content: "### Quadratic Formula\n\nFor $ax^2 + bx + c = 0$:\n\n$$x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}$$"
                    },
                    {
                      name: "Matrix Operations",
                      content: "### Matrix Multiplication\n\n$$\\begin{pmatrix} a & b \\\\ c & d \\end{pmatrix} \\begin{pmatrix} e & f \\\\ g & h \\end{pmatrix} = \\begin{pmatrix} ae+bg & af+bh \\\\ ce+dg & cf+dh \\end{pmatrix}$$"
                    },
                    {
                      name: "Integral Calculus",
                      content: "### Integration by Parts\n\n$$\\int u \\, dv = uv - \\int v \\, du$$\n\n**Example**: $\\int x e^x dx = xe^x - e^x + C$"
                    },
                    {
                      name: "Probability Theory",
                      content: "### Bayes' Theorem\n\n$$P(A|B) = \\frac{P(B|A) \\cdot P(A)}{P(B)}$$\n\nWhere $P(B) \\neq 0$"
                    },
                    {
                      name: "Complex Numbers",
                      content: "### Euler's Formula\n\n$$e^{i\\theta} = \\cos\\theta + i\\sin\\theta$$\n\n**Special case**: $e^{i\\pi} + 1 = 0$"
                    },
                    {
                      name: "Statistics",
                      content: "### Standard Deviation\n\n$$\\sigma = \\sqrt{\\frac{1}{N}\\sum_{i=1}^{N}(x_i - \\mu)^2}$$\n\nWhere $\\mu$ is the mean."
                    }
                  ].map((example, index) => (
                    <Button
                      key={index}
                      variant="outline"
                      className="h-auto p-3 text-left"
                      onClick={() => setCustomContent(example.content)}
                    >
                      <div className="text-sm font-medium">{example.name}</div>
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Usage Documentation */}
            <Card>
              <CardHeader>
                <CardTitle>AceRenderer Usage Guide</CardTitle>
                <CardDescription>
                  How to integrate LaTeX rendering in your components
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold mb-2">Basic Implementation:</h4>
                    <pre className="bg-slate-100 p-3 rounded text-sm overflow-x-auto">
{`import { AceRenderer } from '@/components/ui/ace-renderer';

// Basic usage
<AceRenderer content={mathematicalContent} />

// With options
<AceRenderer 
  content={content}
  enableMath={true}
  compact={false}
  showDebug={false}
/>`}
                    </pre>
                  </div>
                  
                  <Separator />
                  
                  <div>
                    <h4 className="font-semibold mb-2">Chat Integration (Compact Mode):</h4>
                    <pre className="bg-slate-100 p-3 rounded text-sm overflow-x-auto">
{`<AceRenderer 
  content={chatMessage} 
  compact={true} 
  enableMath={true} 
/>`}
                    </pre>
                  </div>
                  
                  <Separator />
                  
                  <div>
                    <h4 className="font-semibold mb-2">Inline Math Only:</h4>
                    <pre className="bg-slate-100 p-3 rounded text-sm overflow-x-auto">
{`import { InlineAceRenderer } from '@/components/ui/ace-renderer';

<InlineAceRenderer content="The area is $\\pi r^2$" />`}
                    </pre>
                  </div>
                  
                  <Separator />
                  
                  <div>
                    <h4 className="font-semibold mb-2">Error Handling:</h4>
                    <pre className="bg-slate-100 p-3 rounded text-sm overflow-x-auto">
{`<AceRenderer 
  content={content}
  onMathError={(error, expression) => {
    console.warn('LaTeX error:', error.message);
    // Handle error (e.g., report to analytics)
  }}
/>`}
                    </pre>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </StaticPageLayout>
  );
};

export default LatexDemoPage;
